import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, Receipt, Users, TrendingUp, Bell,
  Settings, Menu, X, Zap, LogOut, HelpCircle, Scan, FileText, Truck, Globe, DollarSign,
  Wallet, ClipboardList, UserCircle
} from 'lucide-react';

const navItems = [
  { path: '/tenant/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/tenant/packages', label: 'Packages', icon: Package },
  { path: '/tenant/intake', label: 'Intake Scan', icon: Scan },
  { path: '/tenant/delivery-board', label: 'Delivery Board', icon: ClipboardList },
  { path: '/tenant/bom', label: 'BOM', icon: FileText },
  { path: '/tenant/invoices', label: 'Invoices', icon: Receipt },
  { path: '/tenant/finances', label: 'Finances', icon: Wallet },
  { path: '/tenant/shipments', label: 'Shipments', icon: Truck },
  { path: '/tenant/customers', label: 'Customers', icon: Users },
  { path: '/tenant/clients', label: 'Clients', icon: UserCircle },
  { path: '/tenant/reports', label: 'Reports', icon: TrendingUp },
  { path: '/tenant/billing', label: 'Platform Fees', icon: DollarSign },
  { path: '/tenant/website', label: 'Website', icon: Globe },
  { path: '/tenant/notifications', label: 'Notifications', icon: Bell },
  { path: '/tenant/settings', label: 'Settings', icon: Settings },
];

// Mock tenant data
const tenantData = {
  name: 'FastShip Jamaica',
  slug: 'fastship',
  logo: null,
  primaryColor: '#8b5cf6',
  plan: 'Professional',
  prefix: 'FS'
};

export default function TenantLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    navigate('/tenant/login');
  };

  return (
    <div className="app-layout tenant-portal" data-testid="tenant-portal">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`app-sidebar tenant-sidebar ${sidebarOpen ? 'open' : 'collapsed'} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <div className="logo-icon tenant-logo" style={{background: `linear-gradient(135deg, ${tenantData.primaryColor}, #06b6d4)`}}>
              {tenantData.prefix}
            </div>
            {sidebarOpen && (
              <div className="logo-text">
                <span className="logo-title">{tenantData.name}</span>
                <span className="logo-subtitle">Tenant Portal</span>
              </div>
            )}
          </div>
          <button 
            className="sidebar-toggle desktop-only"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <span className="nav-section-label">{sidebarOpen ? 'Menu' : ''}</span>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`nav-${item.path.split('/').pop()}`}
              >
                <item.icon size={20} />
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </NavLink>
            ))}
          </div>

          <div className="nav-section">
            <span className="nav-section-label">{sidebarOpen ? 'Support' : ''}</span>
            <a href="#" className="nav-link" data-testid="nav-help">
              <HelpCircle size={20} />
              {sidebarOpen && <span className="nav-label">Help Center</span>}
            </a>
          </div>
        </nav>

        <div className="sidebar-footer">
          {sidebarOpen && (
            <>
              {/* Plan Badge */}
              <div className="plan-badge">
                <span className="plan-label">Current Plan</span>
                <span className="plan-name">{tenantData.plan}</span>
              </div>
              
              <div className="user-info">
                <div className="user-avatar" style={{background: tenantData.primaryColor}}>
                  JD
                </div>
                <div className="user-details">
                  <span className="user-name">John Doe</span>
                  <span className="user-role">Owner</span>
                </div>
              </div>
            </>
          )}
          <button 
            className="logout-btn" 
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="app-main">
        <header className="app-header tenant-header">
          <div className="header-left">
            <button 
              className="mobile-menu-btn mobile-only"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </button>
            <h1 className="page-title">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="header-right">
            <div className="tenant-indicator">
              <span className="tenant-prefix" style={{background: tenantData.primaryColor}}>{tenantData.prefix}</span>
              <span className="tenant-name">{tenantData.name}</span>
            </div>
            <button className="notification-btn" data-testid="header-notifications">
              <Bell size={20} />
              <span className="notification-badge">5</span>
            </button>
          </div>
        </header>

        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
